type Locale = "de" | "en" | string;

type PropertyType =
  | "APARTMENT"
  | "APARTMENT_BUILDING"
  | "CREATIVE_LIVING"
  | "LAND_PLOT"
  | "SEMI_DETACHED_HOUSE"
  | "SINGLE_FAMILY_HOUSE"
  | "TOWNHOUSE"
  | "TWO_FAMILY_HOUSE"
  | "OTHER";

const propertyTypes: Record<string, Record<PropertyType, string>> = {
  en: {
    APARTMENT: "Apartment",
    APARTMENT_BUILDING: "Apartment building",
    CREATIVE_LIVING: "Creative living",
    LAND_PLOT: "Land plot",
    SEMI_DETACHED_HOUSE: "Semi-detached house",
    SINGLE_FAMILY_HOUSE: "Single-family house",
    TOWNHOUSE: "Townhouse",
    TWO_FAMILY_HOUSE: "Two-family house",
    OTHER: "Unknown",
  },
  de: {
    APARTMENT: "Eigentumswohnung",
    APARTMENT_BUILDING: "Mehrfamilienhaus",
    CREATIVE_LIVING: "Kreatives Leben",
    LAND_PLOT: "Grundstück",
    SEMI_DETACHED_HOUSE: "Doppelhaushälfte",
    SINGLE_FAMILY_HOUSE: "Einfamilienhaus",
    TOWNHOUSE: "Stadt-/Reihenhaus",
    TWO_FAMILY_HOUSE: "Zweifamilienhaus",
    OTHER: "Unbekannt",
  },
};

const localeToLanguage = (locale: Locale): string => {
  if (locale === "en") {
    return "en-EN";
  }
  return "de-DE";
};

export const formatMoney = (
  money: number | string | undefined,
  fractionDigits: boolean = true,
  locale: Locale = "de",
  alwaysShowFractionDigits: boolean = false,
) => {
  if (typeof money === "undefined") {
    return "";
  }

  const language = localeToLanguage(locale);
  const number = isNaN(Number(money)) ? 0 : Number(money);

  const formatterOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency: "EUR",
  };

  if (alwaysShowFractionDigits) {
    formatterOptions.minimumFractionDigits = 2;
    formatterOptions.maximumFractionDigits = 2;
  } else if (!fractionDigits || number % 1 === 0) {
    formatterOptions.minimumFractionDigits = 0;
    formatterOptions.maximumFractionDigits = 0;
  }

  return new Intl.NumberFormat(language, formatterOptions).format(number);
};

export const formatNumber = (
  value: number | string | undefined,
  fractionDigits: boolean = true,
  locale: Locale = "de",
) => {
  if (typeof value === "undefined") {
    return "";
  }

  const language = localeToLanguage(locale);
  const number = isNaN(Number(value)) ? 0 : Number(value);

  const formatterOptions =
    number % 1 === 0 || !fractionDigits
      ? {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      : {};

  return new Intl.NumberFormat(language, formatterOptions).format(number);
};

export const formatPercent = (
  percent: number | string | undefined,
  locale: Locale = "de",
) => {
  if (typeof percent === "undefined") {
    return "";
  }
  const language = localeToLanguage(locale);
  const number = isNaN(Number(percent)) ? 0 : Number(percent) / 100;
  const newNumber = new Intl.NumberFormat(language, {
    style: "percent",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(number);

  return newNumber;
};

export const formatRange = (
  from: string | number,
  to: string | number | undefined,
) => {
  if (!to || to === from) {
    return from;
  }

  return `${from} – ${to}`;
};

export const formatDate = (date: Date, locale: Locale) => {
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDateTime = (date: Date, locale: Locale) => {
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
};

export const toApiDateString = (value: string | Date) => {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

export const formatPropertyType = (
  key: PropertyType | undefined,
  locale: Locale = "de",
): string => {
  const translation = propertyTypes[locale === "immo" ? "de" : locale];

  if (!translation) {
    return "";
  }

  if (!key) {
    return translation.OTHER;
  }

  return translation[key] ?? translation.OTHER;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const decimals = i <= 1 ? 0 : 2;

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
  );
};
